import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useUser } from '../contexts/UserContext';
import { useNavigate } from 'react-router-dom';
import { formatSchoolId } from '../utils/formatUtils';
import MessageModal from '../components/shared/MessageModal';
import CallManager from '../components/shared/CallManager';
import CallService from '../services/callService';
import CallHistoryService, { CallRecord } from '../services/callHistoryService';
import { FaPhone, FaPhoneSlash, FaArrowDown, FaArrowUp, FaExclamationCircle } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import ViewProfileModal from '../components/shared/ViewProfileModal';
import { api } from '../utils/api';

// Types
interface BaseStudent {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  grade: string;
}

interface OtherSchoolStudent extends BaseStudent {
  schoolName: string;
}

interface StudentProfile extends BaseStudent {
  schoolName: string;
  biography?: string;
  strengths?: string[];
  likes?: string[];
  hobbies?: string[];
  profilePhoto?: string;
  careerAspiration?: string;
  cbcCompetencies?: string[];
  skills?: string[];
  extracurricularActivities?: string[];
  projects?: { name: string; description: string; url?: string; competenciesCovered?: string[] }[];
}

interface OtherSchoolsResponse {
  success: boolean;
  data: Record<string, Record<string, OtherSchoolStudent[]>>;
}

interface MessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  recipient: OtherSchoolStudent;
}

interface CallManagerProps {
  callRecord: CallRecord;
  onClose: () => void;
}

// Styled components
const PageContainer = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 1rem;
`;

const Tab = styled.button<{ active: boolean }>`
  padding: 0.75rem 1.5rem;
  border: none;
  background: ${props => props.active ? '#7e22ce' : 'transparent'};
  color: ${props => props.active ? 'white' : '#4b5563'};
  border-radius: 0.5rem;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.active ? '#7e22ce' : '#f3f4f6'};
  }
`;

const SearchContainer = styled.div`
  margin-bottom: 2rem;
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 0.75rem 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: #7e22ce;
    box-shadow: 0 0 0 2px rgba(126, 34, 206, 0.1);
  }
`;

const SchoolSection = styled.div`
  margin-bottom: 2rem;
`;

const SchoolTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 1rem;
`;

const GradeSection = styled.div`
  margin-bottom: 1.5rem;
`;

const GradeTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 500;
  color: #4b5563;
  margin-bottom: 1rem;
`;

const StudentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
`;

const StudentCard = styled.div`
  background: white;
  border-radius: 0.75rem;
  padding: 1.25rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const StudentInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const StudentName = styled.div`
  font-weight: 600;
  color: #1f2937;
`;

const StudentDetails = styled.div`
  color: #6b7280;
  font-size: 0.875rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const Button = styled.button`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 0.375rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;

  &.primary {
    background: #7e22ce;
    color: white;
    &:hover {
      background: #6b21a8;
    }
  }

  &.secondary {
    background: #f3f4f6;
    color: #4b5563;
    &:hover {
      background: #e5e7eb;
    }
  }
`;

const LoadMoreButton = styled(Button)`
  margin: 2rem auto;
  display: block;
  min-width: 200px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  background: #f9fafb;
  border-radius: 0.75rem;
  color: #6b7280;
`;

const StudentsPage = () => {
  const { profile } = useUser();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'classmates' | 'schoolmates' | 'otherSchools'>('classmates');
  const [displayedStudents, setDisplayedStudents] = useState<Record<string, Record<string, OtherSchoolStudent[]>>>({});
  const [currentBatch, setCurrentBatch] = useState(1);
  const [hasMoreStudents, setHasMoreStudents] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStudent, setSelectedStudent] = useState<OtherSchoolStudent | null>(null);
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);
  const [showCallModal, setShowCallModal] = useState(false);
  const [selectedCall, setSelectedCall] = useState<CallRecord | null>(null);

  const STUDENTS_PER_PAGE = 4;

  // Fetch students based on active tab
  const fetchStudents = async (page: number = 1) => {
    if (!profile?._id || !token) return;
    try {
      setIsLoadingMore(true);
      let endpoint = '';
      const searchParam = searchQuery.trim() !== '' ? `&search=${encodeURIComponent(searchQuery.trim())}` : '';
      switch (activeTab) {
        case 'classmates':
          endpoint = `/api/students/classmates?userId=${profile._id}&page=${page}&limit=${STUDENTS_PER_PAGE}${searchParam}`;
          break;
        case 'schoolmates':
          endpoint = `/api/students/schoolmates?userId=${profile._id}&page=${page}&limit=${STUDENTS_PER_PAGE}${searchParam}`;
          break;
        case 'otherSchools':
          endpoint = `/api/students/other-schools?userId=${profile._id}&page=${page}&limit=${STUDENTS_PER_PAGE}${searchParam}`;
          break;
      }

      const response = await fetch(endpoint, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json() as OtherSchoolsResponse;
      if (data.success) {
        setDisplayedStudents(data.data);
        const totalStudents = Object.values(data.data).reduce((total: number, grades: Record<string, OtherSchoolStudent[]>) => {
          return total + Object.values(grades).reduce((gradeTotal: number, students: OtherSchoolStudent[]) => gradeTotal + students.length, 0);
        }, 0);
        setHasMoreStudents(totalStudents === STUDENTS_PER_PAGE);
        setCurrentBatch(page);
      }
    } catch (error) {
      console.error('Error fetching students:', error);
    } finally {
      setIsLoadingMore(false);
      setIsInitialLoad(false);
    }
  };

  // Handle tab change
  const handleTabChange = (tab: 'classmates' | 'schoolmates' | 'otherSchools') => {
    setActiveTab(tab);
    setCurrentBatch(1);
    setSearchQuery('');
    fetchStudents(1);
  };

  // Handle load more
  const handleLoadMore = () => {
    const nextBatch = currentBatch + 1;
    setCurrentBatch(nextBatch);
    fetchStudents(nextBatch);
  };

  // Handle view profile
  const handleViewProfile = async (student: OtherSchoolStudent) => {
    try {
      const response = await api.get(`/students/${student._id}/profile`);
      const data = response.data;
      if (data.success) {
        setSelectedStudent(data.data);
      }
    } catch (error) {
      console.error('Error fetching student profile:', error);
    }
  };

  // Handle message click
  const handleMessageClick = (student: any) => {
    setSelectedStudent(student);
    setIsChatModalOpen(true);
  };

  // Handle start call
  const handleStartCall = (userId: string, userName: string, userEmail: string) => {
    const callService = CallService.getInstance();
    callService.setUserId(userId);
    callService.setUserName(userName);
    setShowCallModal(true);
  };

  // Initial load and search
  useEffect(() => {
    if (profile?._id) {
      fetchStudents(1);
    }
  }, [profile?._id, activeTab, searchQuery]);

  // Use displayedStudents directly since search is now handled server-side
  const filteredStudents = displayedStudents;

  return (
    <PageContainer>
      <TabsContainer>
        <Tab 
          active={activeTab === 'classmates'} 
          onClick={() => handleTabChange('classmates')}
        >
          My Classmates
        </Tab>
        <Tab 
          active={activeTab === 'schoolmates'} 
          onClick={() => handleTabChange('schoolmates')}
        >
          My Schoolmates
        </Tab>
        <Tab 
          active={activeTab === 'otherSchools'} 
          onClick={() => handleTabChange('otherSchools')}
        >
          Other Schools
        </Tab>
      </TabsContainer>

      <SearchContainer>
        <SearchInput
          type="text"
          placeholder="Search students..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </SearchContainer>

      {Object.entries(filteredStudents).map(([schoolName, grades]) => (
        <SchoolSection key={schoolName}>
          <SchoolTitle>{schoolName}</SchoolTitle>
          {Object.entries(grades).map(([grade, students]) => (
            <GradeSection key={grade}>
              <GradeTitle>Grade {grade}</GradeTitle>
              <StudentGrid>
                {students.map((student) => (
                  <StudentCard key={student._id}>
                    <StudentInfo>
                      <StudentName>
                        {student.firstName} {student.lastName}
                      </StudentName>
                      <StudentDetails>
                        <div>{student.email}</div>
                        {student.schoolName && <div>{student.schoolName}</div>}
                      </StudentDetails>
                    </StudentInfo>
                    <ButtonGroup>
                      <Button 
                        className="primary"
                        onClick={() => handleStartCall(student._id, `${student.firstName} ${student.lastName}`, student.email)}
                      >
                        <FaPhone /> Call
                      </Button>
                      <Button 
                        className="secondary"
                        onClick={() => handleViewProfile(student)}
                      >
                        View Profile
                      </Button>
                      <Button 
                        className="secondary"
                        onClick={() => handleMessageClick(student)}
                      >
                        Message
                      </Button>
                    </ButtonGroup>
                  </StudentCard>
                ))}
              </StudentGrid>
            </GradeSection>
          ))}
        </SchoolSection>
      ))}

      {Object.keys(filteredStudents).length === 0 && !isLoadingMore && (
        <EmptyState>
          No students found
        </EmptyState>
      )}

      {hasMoreStudents && (
        <LoadMoreButton 
          className="primary"
          onClick={handleLoadMore} 
          disabled={isLoadingMore}
        >
          {isLoadingMore ? 'Loading...' : 'Load More Students'}
        </LoadMoreButton>
      )}

      {/* Modals */}
      {isChatModalOpen && selectedStudent && profile && (
        <MessageModal
          isOpen={isChatModalOpen}
          onClose={() => setIsChatModalOpen(false)}
          sender={{
            id: profile._id,
            name: `${profile.firstName} ${profile.lastName}`,
            role: 'student'
          }}
          receiver={{
            id: selectedStudent._id,
            name: `${selectedStudent.firstName} ${selectedStudent.lastName}`,
            role: 'student'
          }}
        />
      )}

      {showCallModal && selectedCall && (
        <CallManager
          userId={selectedCall.callerId}
          userName={selectedCall.callerName}
        />
      )}
    </PageContainer>
  );
};

export default StudentsPage; 